/**
 * step-knowledge.tsx — Bước 2 (bỏ qua được): dạy kiến thức NGAY TRONG TRANG, không mở tab / hộp mới.
 *  - Chọn kho tri thức và gắn vào nhân viên AI (`toolsJson.search_knowledge.guardrail.knowledgeBaseIds`). "Tạo kho mới" mở
 *    màn Kho tri thức ở TAB MỚI (sẵn hộp tạo kho); bước này giữ nguyên, có nút "Tải lại" danh sách kho (+ tự làm mới khi
 *    quay lại tab).
 *  - Thêm vào kho đầu tiên đã gắn: dán link website / Google Docs, tải file, gõ tay (cùng API của hộp "Thêm kiến thức":
 *    `/knowledge/ingest/url`, `/knowledge/ingest/file`, `POST /knowledge`), theo dõi tiến độ học (số đoạn đã nhúng).
 *  - Chọn nguồn sản phẩm AI được tra (`toolsJson.dataSources.products`, hàm của data-sources-lib); "Kết nối nguồn" mở
 *    màn Kết nối ứng dụng ở tab mới + "Tải lại" danh sách nguồn.
 *  - "Hỏi thử" gửi câu vào khung chat thử bên phải — câu trả lời hiện nguồn đã trích.
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import {
  ArrowLeft, ArrowRight, CheckCircle2, FileUp, Link2, Loader2, MessageSquareText, PencilLine, PlugZap, Plus, ShoppingBag, XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Textarea } from '@/components/ui/misc'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { api, apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { aiKeys, useAiDataSources, type AiBotToolsJson } from '@/hooks/use-ai'
import { KB_BASES_KEY, useKnowledgeBases } from '@/hooks/use-knowledge-bases'
import { fetchKnowledgeEntry, ingestKnowledgeUrl, uploadKnowledgeFile, type KnowledgeEntry } from '@/hooks/use-knowledge'
import { KB_ACCEPT, checkKbFile, embedStatusInfo, formatBytes, isKbBusy } from '@/lib/kb-status'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { CONNECT_APPS_PATH, PRODUCTS_PATH } from '@/components/ai/open-target-lib'
import { ReloadButton } from '@/components/ai/reload-button'
import { newKnowledgeBaseUrl } from '../knowledge/kb-lib'
import { currentSelection, sourceStats, toggleSource } from '../data-sources-lib'
import { updateWizardBot, type WizardBot } from './wizard-api'
import { botKnowledgeBaseIds, knowledgeReady, withKnowledgeBases } from './wizard-lib'

type ItemState = 'uploading' | 'processing' | 'done' | 'error'
interface Item { key: string; name: string; state: ItemState; progress: number; entryId?: string; chunks?: number; message?: string; size?: number }

let seq = 0

export function StepKnowledge({ bot, onBotChange, onAsk, onBack, onNext }: {
  bot: WizardBot
  onBotChange: () => Promise<unknown>
  /** Gửi câu hỏi thử vào khung chat bên phải. */
  onAsk: (text: string) => void
  onBack: () => void
  onNext: () => void
}) {
  const { t } = useTranslation('aiWizard')
  const qc = useQueryClient()
  const { data: bases, isLoading: basesLoading } = useKnowledgeBases()
  const attached = botKnowledgeBaseIds(bot)
  const targetBase = bases?.find((b) => b.id === attached[0]) ?? null
  const [savingTools, setSavingTools] = useState(false)
  const [items, setItems] = useState<Item[]>([])
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [busy, setBusy] = useState(false)
  const [question, setQuestion] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  const ready = knowledgeReady(bot, bases)
  const newKbName = `Kiến thức — ${bot.name}` // i18n-ignore: tên kho LƯU (tiếng Việt)

  const patch = (key: string, p: Partial<Item>) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...p } : i)))

  // Theo dõi mục đang học (đọc tài liệu + nhúng) mỗi 2 giây.
  const tracking = items.filter((i) => i.state === 'processing' && i.entryId).map((i) => i.entryId).join(',')
  useEffect(() => {
    if (!tracking) return
    const ids = tracking.split(',')
    const timer = setInterval(async () => {
      const rows = (await Promise.all(ids.map((id) => fetchKnowledgeEntry(id).catch(() => null)))).filter((r): r is KnowledgeEntry => !!r)
      let changed = false
      setItems((prev) => prev.map((it) => {
        const r = rows.find((x) => x.id === it.entryId)
        if (!r || it.state !== 'processing' || isKbBusy(r.embedStatus)) return it
        changed = true
        if (r.embedStatus === 'ready') return { ...it, state: 'done', chunks: r.chunkCount ?? 0, name: r.title || it.name }
        if (r.embedStatus === 'no_key') return { ...it, state: 'done', chunks: 0, name: r.title || it.name, message: embedStatusInfo(r).label }
        return { ...it, state: 'error', message: embedStatusInfo(r).label }
      }))
      if (changed) void qc.invalidateQueries({ queryKey: ['knowledge'] })
    }, 2_000)
    return () => clearInterval(timer)
  }, [tracking, qc])

  async function saveTools(next: AiBotToolsJson, okMsg?: string) {
    setSavingTools(true)
    try {
      await updateWizardBot(bot.id, { toolsJson: next })
      await onBotChange()
      if (okMsg) toast.success(okMsg)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSavingTools(false)
    }
  }

  function toggleBase(id: string, on: boolean) {
    const ids = on ? [...attached, id] : attached.filter((x) => x !== id)
    void saveTools(withKnowledgeBases(bot.toolsJson, ids))
  }

  function addFiles(list: FileList) {
    const files = Array.from(list)
    const bad = files.map((f) => ({ f, err: checkKbFile(f) })).filter((x) => x.err)
    if (bad.length) {
      setItems((prev) => [...prev, ...bad.map(({ f, err }): Item => ({ key: `f${++seq}`, name: f.name, size: f.size, state: 'error', progress: 0, message: err ?? undefined }))])
    }
    void uploadFiles(files.filter((f) => !checkKbFile(f)))
  }

  /** Tải lần lượt từng file vào kho nhận tài liệu mới. */
  async function uploadFiles(files: File[]) {
    if (!targetBase) return
    for (const f of files) {
      const key = `u${++seq}`
      setItems((prev) => [...prev, { key, name: f.name, size: f.size, state: 'uploading', progress: 0 }])
      try {
        const entry = await uploadKnowledgeFile(f, { knowledgeBaseId: targetBase.id, onProgress: (pct) => patch(key, { progress: pct }) })
        patch(key, { state: 'processing', entryId: entry.id })
      } catch (err) {
        patch(key, { state: 'error', message: apiError(err) })
      }
    }
  }

  async function submitLink() {
    const u = url.trim()
    if (!u || !targetBase) return
    if (/docs\.google\.com\/spreadsheets\//i.test(u)) { toast.info(t('knowledge.sheetsInfo')); return }
    const key = `l${++seq}`
    setItems((prev) => [...prev, { key, name: u, state: 'uploading', progress: 100 }])
    setUrl('')
    try {
      const entry = await ingestKnowledgeUrl(u, null, targetBase.id)
      patch(key, { state: 'processing', entryId: entry.id, name: entry.title || u })
    } catch (err) {
      patch(key, { state: 'error', message: apiError(err) })
    }
  }

  async function submitText() {
    if (!content.trim() || !targetBase) return
    setBusy(true)
    try {
      const ttl = title.trim()
      const { data } = await api.post<{ data: KnowledgeEntry }>('/knowledge', {
        type: 'description', format: 'article', risk: 'low', source: 'staff_manual',
        title: ttl || null, content: ttl ? `# ${ttl}\n\n${content.trim()}` : content.trim(), categoryId: null, knowledgeBaseId: targetBase.id,
      })
      setItems((prev) => [...prev, { key: `t${++seq}`, name: ttl || t('knowledge.manualContent'), state: 'processing', progress: 100, entryId: data.data.id }])
      setTitle(''); setContent('')
      void qc.invalidateQueries({ queryKey: ['knowledge'] })
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }

  const learnedChunks = items.filter((i) => i.state === 'done').reduce((n, i) => n + (i.chunks ?? 0), 0)
  const active = items.filter((i) => i.state === 'uploading' || i.state === 'processing').length

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-medium">{t('knowledge.title')} <span className="text-xs font-normal text-muted-foreground">{t('knowledge.skippable')}</span></h2>
        <p className="text-sm text-muted-foreground">{t('knowledge.intro', { name: bot.name })}</p>
      </div>

      {/* Kho tri thức */}
      <section className="space-y-2 rounded-xl border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="text-sm">{t('knowledge.basesLabel', { name: bot.name })}</Label>
          <div className="flex flex-wrap items-center gap-1">
            <ReloadButton queryKeys={[KB_BASES_KEY]} what={t('knowledge.basesWhat')} />
            <OpenInNewTab to={newKnowledgeBaseUrl(newKbName)} variant="outline" size="sm" title={t('knowledge.newBaseTitle')}>
              <Plus className="h-3.5 w-3.5" /> {t('knowledge.newBase')}
            </OpenInNewTab>
          </div>
        </div>
        {basesLoading ? (
          <p className="text-xs text-muted-foreground">{t('knowledge.loadingBases')}</p>
        ) : !bases?.length ? (
          <p className="text-xs text-muted-foreground">{t('knowledge.noBases')}</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {bases.map((b) => {
              const on = attached.includes(b.id)
              return (
                <li key={b.id}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm">
                    <Checkbox checked={on} disabled={savingTools} onCheckedChange={(v) => toggleBase(b.id, v === true)} />
                    <span className="min-w-0 flex-1 truncate">📚 {b.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{t('knowledge.baseCounts', { articles: b.articleCount, qa: b.qaCount })}</span>
                    {on && attached[0] === b.id && <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">{t('knowledge.receivesNew')}</span>}
                  </label>
                </li>
              )
            })}
          </ul>
        )}
        <p className="text-[11px] text-muted-foreground">
          {attached.length ? t('knowledge.onlyBases', { count: attached.length }) : t('knowledge.noneSelected')}
          {' '}{t('knowledge.reloadHint')}
        </p>
      </section>

      {/* Thêm kiến thức */}
      <section className="space-y-3 rounded-xl border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-medium">{targetBase ? <Trans t={t} i18nKey="knowledge.addTo" values={{ name: targetBase.name }} components={{ b: <span className="text-primary" /> }} /> : t('knowledge.add')}</h3>
          {(learnedChunks > 0 || active > 0) && (
            <span className="text-xs text-muted-foreground">
              {active > 0 ? t('knowledge.learning', { count: active }) : ''}{learnedChunks > 0 ? t('knowledge.embedded', { count: learnedChunks }) : ''}
            </span>
          )}
        </div>
        {!targetBase ? (
          <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">{t('knowledge.pickBaseFirst')}</p>
        ) : (
          <Tabs defaultValue="link">
            <TabsList className="h-auto flex-wrap justify-start">
              <TabsTrigger value="link" className="gap-1.5"><Link2 className="h-3.5 w-3.5" /> {t('knowledge.tabLink')}</TabsTrigger>
              <TabsTrigger value="file" className="gap-1.5"><FileUp className="h-3.5 w-3.5" /> {t('knowledge.tabFile')}</TabsTrigger>
              <TabsTrigger value="text" className="gap-1.5"><PencilLine className="h-3.5 w-3.5" /> {t('knowledge.tabText')}</TabsTrigger>
              <TabsTrigger value="product" className="gap-1.5"><ShoppingBag className="h-3.5 w-3.5" /> {t('knowledge.tabProduct')}</TabsTrigger>
            </TabsList>
            <TabsContent value="link" className="space-y-2">
              <div className="flex gap-2">
                <Input
                  value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t('knowledge.urlPlaceholder')}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submitLink() } }} aria-label={t('knowledge.urlAria')}
                />
                <Button onClick={() => void submitLink()} disabled={!url.trim()}><Link2 className="h-4 w-4" /> {t('common:actions.add')}</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">{t('knowledge.urlHint')}</p>
            </TabsContent>
            <TabsContent value="file" className="space-y-2">
              <button
                type="button" onClick={() => fileRef.current?.click()}
                className="flex w-full flex-col items-center gap-1 rounded-lg border-2 border-dashed px-4 py-6 text-center hover:border-primary/60 hover:bg-muted/40"
              >
                <FileUp className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium">{t('knowledge.chooseFile')}</span>
                <span className="text-xs text-muted-foreground">{t('knowledge.fileTypes')}</span>
              </button>
              <input ref={fileRef} type="file" multiple accept={KB_ACCEPT} hidden onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = '' }} />
            </TabsContent>
            <TabsContent value="text" className="space-y-2">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('knowledge.titlePlaceholder')} aria-label={t('knowledge.titleAria')} />
              <Textarea className="min-h-32" value={content} onChange={(e) => setContent(e.target.value)} placeholder={t('knowledge.contentPlaceholder')} aria-label={t('knowledge.contentAria')} />
              <div className="flex justify-end">
                <Button onClick={() => void submitText()} disabled={busy || !content.trim()}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PencilLine className="h-4 w-4" />} {t('knowledge.add')}
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="product">
              <ProductSources bot={bot} saving={savingTools} onSave={(tools) => void saveTools(tools)} />
            </TabsContent>
          </Tabs>
        )}
        {items.length > 0 && (
          <ul className="space-y-1.5">
            {items.map((it) => <ItemRow key={it.key} it={it} />)}
          </ul>
        )}
      </section>

      {/* Hỏi thử */}
      <section className="space-y-2 rounded-xl border p-4">
        <Label htmlFor="wz-ask" className="text-sm">{t('knowledge.askLabel')}</Label>
        <div className="flex gap-2">
          <Input
            id="wz-ask" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t('knowledge.askPlaceholder')}
            onKeyDown={(e) => { if (e.key === 'Enter' && question.trim()) { e.preventDefault(); onAsk(question); setQuestion('') } }}
          />
          <Button variant="outline" disabled={!question.trim()} onClick={() => { onAsk(question); setQuestion('') }}>
            <MessageSquareText className="h-4 w-4" /> {t('knowledge.ask')}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">{t('knowledge.askHint')}</p>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" /> {t('common:actions.back')}</Button>
        <div className="flex items-center gap-2">
          {ready
            ? <span className="flex items-center gap-1 text-xs text-success"><CheckCircle2 className="h-3.5 w-3.5" /> {t('knowledge.ready')}</span>
            : <Button variant="ghost" onClick={onNext}>{t('knowledge.skip')}</Button>}
          <Button onClick={onNext} title={active > 0 ? t('knowledge.nextTitle') : undefined}>
            {t('knowledge.next')} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function ItemRow({ it }: { it: Item }) {
  const { t } = useTranslation('aiWizard')
  const icon = it.state === 'done' ? <CheckCircle2 className="h-4 w-4 text-success" />
    : it.state === 'error' ? <XCircle className="h-4 w-4 text-destructive" />
      : <Loader2 className="h-4 w-4 animate-spin text-primary" />
  const text = it.state === 'uploading' ? t('knowledge.item.uploading', { progress: it.progress })
    : it.state === 'processing' ? t('knowledge.item.processing')
        : it.state === 'done' ? (it.message ?? (it.chunks ? t('knowledge.item.learned', { count: it.chunks }) : t('knowledge.item.done')))
          : it.message ?? t('common:status.error')
  return (
    <li className="flex items-center gap-2 rounded-md border px-2.5 py-1.5">
      {icon}
      <span className="min-w-0 flex-1 truncate text-sm">{it.name}</span>
      {it.size != null && <span className="shrink-0 text-[11px] text-muted-foreground">{formatBytes(it.size)}</span>}
      <span className={cn('shrink-0 text-xs', it.state === 'done' ? 'text-success' : it.state === 'error' ? 'text-destructive' : 'text-muted-foreground')}>{text}</span>
    </li>
  )
}

/** Nguồn sản phẩm AI được tra (đồng bộ / nhập trên eCDP). "Kết nối nguồn" mở tab mới; "Tải lại" chỉ tải lại danh sách nguồn. */
function ProductSources({ bot, saving, onSave }: { bot: WizardBot; saving: boolean; onSave: (tools: AiBotToolsJson) => void }) {
  const { t } = useTranslation('aiWizard')
  const { data, isLoading } = useAiDataSources()
  const sources = (data?.sources ?? []).filter((s) => s.products)
  const sel = currentSelection(bot.toolsJson, data?.sources ?? [])
  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs font-medium">{t('knowledge.products.title')}</p>
      <div className="flex flex-wrap items-center gap-1">
        <ReloadButton queryKeys={[aiKeys.dataSources]} what={t('knowledge.products.what')} />
        <OpenInNewTab to={CONNECT_APPS_PATH} variant="outline" size="sm" title={t('knowledge.products.connectTitle')}>
          <PlugZap className="h-3.5 w-3.5" /> {t('knowledge.products.connect')}
        </OpenInNewTab>
      </div>
    </div>
  )
  if (isLoading) return <div className="space-y-2">{toolbar}<p className="text-xs text-muted-foreground">{t('knowledge.products.loading')}</p></div>
  if (!sources.length) {
    return (
      <div className="space-y-2">
        {toolbar}
        <p className="text-xs text-muted-foreground">
          <Trans t={t} i18nKey="knowledge.products.empty" components={{ add: <OpenInNewTab to={PRODUCTS_PATH}>{null}</OpenInNewTab> }} />
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {toolbar}
      <p className="text-xs text-muted-foreground">{t('knowledge.products.pick', { name: bot.name })}</p>
      <ul className="divide-y rounded-lg border">
        {sources.map((s) => (
          <li key={s.key}>
            <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm">
              <Checkbox
                checked={sel.products.includes(s.key)} disabled={saving || s.status === 'unauthorized'}
                onCheckedChange={(v) => onSave(toggleSource(bot.toolsJson, data?.sources ?? [], 'products', s.key, v === true))}
              />
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{s.status === 'unauthorized' ? t('knowledge.products.unauthorized') : sourceStats(s)}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  )
}
